import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './utils/auth';
import { NotificationProvider } from './utils/notifications';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import GeneratorReport from './components/GeneratorReport';
import MaintenanceDashboard from './pages/MaintenanceDashboard';
import MaintenanceTemplateManager from './components/MaintenanceTemplateManager';
import MaintenanceTemplatesList from './pages/MaintenanceTemplatesList';
import MaintenanceScheduleForm from './components/MaintenanceScheduleForm';
import MaintenanceRecordForm from './components/MaintenanceRecordForm';
import MaintenanceReports from './components/MaintenanceReports';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  console.log('ProtectedRoute - isLoading:', isLoading, 'isAuthenticated:', isAuthenticated, 'user:', user);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  console.log('PublicRoute - isLoading:', isLoading, 'isAuthenticated:', isAuthenticated, 'user:', user);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? <Navigate to="/" replace /> : <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <GeneratorReport />
          </ProtectedRoute>
        }
      />
      {/* Maintenance Routes */}
      <Route
        path="/maintenance"
        element={
          <ProtectedRoute>
            <MaintenanceDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/maintenance/templates"
        element={
          <ProtectedRoute>
            <MaintenanceTemplatesList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/maintenance/templates/new"
        element={
          <ProtectedRoute>
            <MaintenanceTemplateManager />
          </ProtectedRoute>
        }
      />
      <Route
        path="/maintenance/templates/:id"
        element={
          <ProtectedRoute>
            <MaintenanceTemplateManager />
          </ProtectedRoute>
        }
      />
      <Route
        path="/maintenance/schedules/new"
        element={
          <ProtectedRoute>
            <MaintenanceScheduleForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/maintenance/schedules/:id"
        element={
          <ProtectedRoute>
            <MaintenanceScheduleForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/maintenance/records/new"
        element={
          <ProtectedRoute>
            <MaintenanceRecordForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/maintenance/records/:id"
        element={
          <ProtectedRoute>
            <MaintenanceRecordForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/maintenance/reports"
        element={
          <ProtectedRoute>
            <MaintenanceReports />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <NotificationProvider>
        <Router>
          <div className="App">
            <AppRoutes />
          </div>
        </Router>
      </NotificationProvider>
    </AuthProvider>
  );
};

export default App;